// ---------------------------------------------------------------------------
// SpeedyDock SDK — Inline embed
// ---------------------------------------------------------------------------

import { SDK_PREFIX } from "./styles";
import type { InlineOptions, EmbedInstance, EmbedEventType, EmbedEventHandler } from "./types";
import {
  buildEmbedUrl,
  resolveElement,
  createElement,
  createIframe,
  createSpinner,
  hideSpinner,
  EventEmitter,
  listenToIframe,
  detectTopStickyOffset,
} from "./utils";
import { isDebugEnabled, debugLog, debugRecord, now, setDebug } from "./debug";

export function createInlineEmbed(
  baseUrl: string,
  shortName: string,
  target: string | HTMLElement,
  options: InlineOptions = {},
): EmbedInstance {
  const container = resolveElement(target);
  if (!container) throw new Error(`[SpeedyDock] Target "${target}" not found.`);

  const wrapper = createElement("div", `${SDK_PREFIX}-inline`);
  if (options.height) wrapper.style.height = options.height;

  const spinner = createSpinner();
  wrapper.appendChild(spinner);

  const src = buildEmbedUrl(baseUrl, shortName, options);
  const iframe = createIframe(src, "eager");
  wrapper.appendChild(iframe);

  const emitter = new EventEmitter();
  const autoResize = options.autoResize !== false;

  // Opt-in diagnostics for the navigate→resize→scroll cycle. Can also be
  // toggled at runtime on a live host page (see sdk/src/debug.ts).
  if (options.debug) setDebug(true);

  // Assigned by the scrollOnNavigate block below when scroll handling is
  // enabled. Lets the embedded app request the host to scroll it into view
  // (e.g. to reveal a top-anchored error toast) via a postMessage.
  let requestScrollIntoView: (() => void) | undefined;

  const unlisten = listenToIframe(iframe, emitter, {
    onReady: () => hideSpinner(spinner),
    onScrollRequest: () => requestScrollIntoView?.(),
    autoResize,
  });

  // When the embedded app navigates between steps the iframe grows/shrinks and
  // the *host page* is what scrolls (the iframe never scrolls internally under
  // auto-resize). Without this, a visitor who scrolled down to reach the widget
  // stays parked mid-page and lands below the top of the next step.
  //
  // The embed's top edge is invariant to the iframe's height change, so we wait
  // for the post-navigation resize to settle before scrolling. Starting a scroll
  // while the iframe is still changing height leaves a smooth scroll parked
  // partway and lands at an inconsistent spot.
  const scrollOnNavigate = options.scrollOnNavigate !== false;
  if (scrollOnNavigate) {
    let pendingScroll = false;
    // Fires once the post-navigation resize storm goes quiet.
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    // Guarantees we scroll even if the destination never resizes, and caps how
    // long we wait for a still-growing page before committing.
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;

    // How long the iframe height must stay quiet before we treat the layout as
    // settled, and the hard cap on total wait after a navigation.
    const SETTLE_MS = 120;
    const MAX_WAIT_MS = 700;

    // Debug-only telemetry describing the in-flight navigate→resize→scroll cycle.
    let cycle:
      | {
          path: string;
          navAt: number;
          scrollYAtNav: number;
          resizes: { t: number; height: number }[];
        }
      | undefined;

    const clearTimers = () => {
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = undefined;
      }
      if (deadlineTimer) {
        clearTimeout(deadlineTimer);
        deadlineTimer = undefined;
      }
    };

    // Cheap geometry read (debug only). The wrapper's top is *supposed* to be
    // invariant to the iframe's height change; the post-commit sampler verifies
    // whether it actually is.
    const geometry = () => {
      const rect = wrapper.getBoundingClientRect();
      return {
        rectTop: Math.round(rect.top),
        scrollY: Math.round(window.scrollY),
        anchorTop: Math.round(rect.top + window.scrollY),
        scrollHeight: Math.round(document.documentElement.scrollHeight),
      };
    };

    // After committing the scroll, keep sampling the wrapper's absolute anchor
    // and the scroll position so we can tell apart the competing explanations:
    //   • anchorDriftPx > 0 / reachedCommitTarget false ⇒ we scrolled to a
    //     target that kept moving as the page grew (resize→scroll race).
    //   • anchorDriftPx ≈ 0 but residualPx large & constant ⇒ the layout was
    //     stable and the offset/target itself was simply wrong.
    const sampleAfterCommit = (
      via: "settle" | "deadline",
      offset: number,
      commitTarget: number,
      commitSummary: Record<string, unknown>,
    ) => {
      const commitAt = now();
      const samples: Record<string, number>[] = [];
      const take = () => {
        const g = geometry();
        const idealTarget = Math.max(0, g.anchorTop - offset);
        samples.push({
          t: Math.round(now() - commitAt),
          rectTop: g.rectTop,
          scrollY: g.scrollY,
          anchorTop: g.anchorTop,
          idealTarget,
          deltaFromIdeal: g.scrollY - idealTarget,
          scrollHeight: g.scrollHeight,
        });
      };
      const SAMPLE_OFFSETS = [80, 200, 400, 700, 1000, 1300];
      SAMPLE_OFFSETS.forEach((ms) => setTimeout(take, ms));
      setTimeout(() => {
        const anchors = samples.map((s) => s.anchorTop);
        const heights = samples.map((s) => s.scrollHeight);
        const final = samples[samples.length - 1];
        const summary = {
          ...commitSummary,
          kind: "scroll-cycle",
          committedVia: via,
          commitTarget: Math.round(commitTarget),
          // (c) Did the embed's absolute top keep moving after we scrolled?
          anchorDriftPx: anchors.length
            ? Math.max(...anchors) - Math.min(...anchors)
            : 0,
          // Did the page keep growing after commit (resize storm still running)?
          scrollHeightGrowthPx: heights.length
            ? Math.max(...heights) - Math.min(...heights)
            : 0,
          // How far below the true embed top we ended up once settled
          // (positive ⇒ we're parked that many px too low — the reported bug).
          residualPx: final ? final.deltaFromIdeal : undefined,
          // False ⇒ the smooth scroll never reached the target we set, i.e. a
          // reflow during the animation curtailed it.
          reachedCommitTarget: samples.some(
            (s) => Math.abs(s.scrollY - commitTarget) <= 3,
          ),
          samples,
        };
        debugLog("scroll-cycle summary", summary);
        debugRecord(summary);
      }, SAMPLE_OFFSETS[SAMPLE_OFFSETS.length - 1] + 60);
    };

    const commitScroll = (via: "settle" | "deadline") => {
      if (!pendingScroll) return;
      pendingScroll = false;
      clearTimers();

      const rect = wrapper.getBoundingClientRect();
      const scrollY = window.scrollY;
      // Stop just below a host-page sticky/fixed header so the embed's top edge
      // isn't hidden behind it. An explicit scrollOffset always wins.
      const offset = options.scrollOffset ?? detectTopStickyOffset(wrapper);
      const anchorTop = rect.top + scrollY;
      const target = Math.max(0, anchorTop - offset);
      const willScroll = Math.abs(target - scrollY) >= 2;
      let smooth = false;
      if (willScroll) {
        const prefersReduced =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        smooth = !prefersReduced;
        window.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
      }

      if (isDebugEnabled()) {
        const summary = {
          path: cycle?.path,
          via,
          commitElapsedMs: cycle ? Math.round(now() - cycle.navAt) : 0,
          resizeCount: cycle?.resizes.length ?? 0,
          stormDurationMs: cycle
            ? Math.round(cycle.resizes[cycle.resizes.length - 1]?.t ?? 0)
            : 0,
          offset,
          rectTop: Math.round(rect.top),
          scrollY: Math.round(scrollY),
          anchorTop: Math.round(anchorTop),
          scrollHeight: Math.round(document.documentElement.scrollHeight),
          target: Math.round(target),
          willScroll,
          smooth,
        };
        debugLog(`commit via ${via}`, summary);
        sampleAfterCommit(via, offset, target, summary);
      }
    };

    // Schedule a scroll using the same settle/commit machinery as navigation.
    // Reused for both in-app navigations and explicit scroll-into-view requests
    // (e.g. surfacing a top-anchored error toast after a failed purchase).
    const scheduleScroll = () => {
      pendingScroll = true;
      clearTimers();
      // If the layout is already the right height, no resize fires — scroll
      // anyway once the settle window elapses.
      settleTimer = setTimeout(() => commitScroll("settle"), SETTLE_MS);
      // The destination page keeps growing as async content (calendar,
      // availability, images) loads. Don't commit to a scroll target measured
      // mid-render, but don't wait forever either.
      deadlineTimer = setTimeout(() => commitScroll("deadline"), MAX_WAIT_MS);
    };

    // Let the embedded app request the host to scroll it into view.
    requestScrollIntoView = scheduleScroll;

    emitter.on("navigate", (data) => {
      scheduleScroll();

      if (isDebugEnabled()) {
        cycle = {
          path: data?.path ?? window.location.pathname,
          navAt: now(),
          scrollYAtNav: Math.round(window.scrollY),
          resizes: [],
        };
        debugLog("navigate", {
          path: cycle.path,
          scrollYAtNav: cycle.scrollYAtNav,
          settleMs: SETTLE_MS,
          maxWaitMs: MAX_WAIT_MS,
        });
      }
    });

    // Each resize means the iframe is still taking its new height. Reset the
    // settle timer so we only scroll once heights have gone quiet — measuring
    // the wrapper's final position rather than an intermediate one.
    emitter.on("resize", (data) => {
      if (!pendingScroll) return;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => commitScroll("settle"), SETTLE_MS);

      if (cycle && isDebugEnabled()) {
        cycle.resizes.push({
          t: Math.round(now() - cycle.navAt),
          height: Math.round(data?.height ?? 0),
        });
      }
    });
  }

  container.appendChild(wrapper);

  return {
    destroy() {
      unlisten();
      wrapper.remove();
    },
    on<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.on(event, handler);
    },
    off<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.off(event, handler);
    },
    navigate(path: string) {
      iframe.contentWindow?.postMessage(
        { type: "speedydock:parent-navigate", data: { path } },
        "*",
      );
    },
  };
}
